import { ImageMediaContent, Media, UserContent, UserContentInput } from "./strapi-model";

const fetch = require("node-fetch");

const apiBaseUrlTmp = process.env.PRIVATE_STRAPI_API_BASE_URL || process.env.NEXT_PUBLIC_STRAPI_API_BASE_URL;
if (! apiBaseUrlTmp) throw new Error("Strapi API base URL is not specified.");
export const API_BASE_URL: string = apiBaseUrlTmp.replace(/\/+$/, "");

export interface UserContentModel<C extends UserContent> {
  readonly modelName: string;
}

export interface CollectionUserContentModel<C extends UserContent> extends UserContentModel<C> {
  readonly collectionName: string;
}

export class SingletonUserContentService<C extends UserContent> {
  readonly model: UserContentModel<C>;

  constructor(readonly userContentModel: UserContentModel<C>) {
    this.model = userContentModel;
  }

  async get(): Promise<C | null> {
    const resp = await fetch(`${API_BASE_URL}/${this.model.modelName}`);
    if (resp.ok) return await resp.json() as C;
    if (resp.status === 404) return null;
    throw await createResponseError(resp);
  }
}

export class CollectionUserContentService<C extends UserContent> {
  readonly model: CollectionUserContentModel<C>;

  constructor(readonly userContentModel: CollectionUserContentModel<C>) {
    this.model = userContentModel;
  }

  async getAll(): Promise<C[]> {
    const resp = await fetch(`${API_BASE_URL}/${this.model.collectionName}`);
    if (resp.ok) return await resp.json() as C[];
    throw await createResponseError(resp);
  }

  async getById(id: number): Promise<C | null> {
    if (! Number.isInteger(id)) throw new Error("The specified ID must be an integer.");

    const resp = await fetch(`${API_BASE_URL}/${this.model.collectionName}/${id}`);
    if (resp.ok) return await resp.json() as C;
    if (resp.status === 404) return null;
    throw await createResponseError(resp);
  }

  async create(data: UserContentInput<C>): Promise<C> {
    const resp = await fetch(`${API_BASE_URL}/${this.model.collectionName}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    });
    if (resp.ok) return await resp.json();
    throw await createResponseError(resp);
  }
}

async function createResponseError(response: Response): Promise<Error> {
  const msg = await response.text();
  return new Error(`Unexpected error returned from the server side. Status: ${response.status}, Body: ${msg}`);
}

export const resolveMediaUrl = (media: Media): string => {
  return API_BASE_URL + media.url;
}

export const extractImageInfo = (image: ImageMediaContent): { src: string, alt: string } => {
  return {
    src: resolveMediaUrl(image),
    alt: image.alternativeText
  }
}
